import React from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  IDetailsRowProps,
} from "@fluentui/react/lib/DetailsList";
import type { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { SkeletonCell, TicketProp, hierarchialStatusName } from './TicketListBoard';
import { withArchived } from '../../utils/archivedPickers';
import { EMPTY_CELL, hoverTitle } from './emptyCell';
import { NO_SELECT_ON_FOCUS, cellEditingOn, reportFailure } from './inlineCell';
import { LeavingRows, leaveClass, rowLeaver } from './rowLeave';
import { ConvertDateToLocale } from '../../common/ConvertDate';
import { AppStateValue, useSetState } from '../../../AppState';
import { UserAvatar } from '../UserAvatar';
import { getStatus, isCloseFilter } from '../../../tikit/ticketHelper';
import { TicketStatus } from '../TicketCardTemplate';
import { TicketProps } from '../../interfaces/ticketproperties.interface';
import { Link, RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { SpeakerPersonIcon, ProviderConsumer as FluentUIThemeConsumer, AcceptIcon, Tooltip } from '@fluentui/react-northstar';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { PRODUCT_LICENSE, ListColumnKey, MyWorkListViewType, markdownComponents, NON_SORTABLE_COLUMNS, STATUS_GUID } from '../../utils/constants';
import { getCachedFeature } from '../../cache/FeatureCache';
import { EnumSortOrderType } from '../../../tikit/toolbar/CustomViewContextProvider';
import { GroupUsersIcon } from '../../../tikit/Request/EditRequestDetails/GroupUsersCard/GroupUsersIcon';
import { TicketLifecyclePhaseElement } from '../TicketLifecycleTab/TicketLifecyclePhaseRibbon';
import { SlaPills } from '../SlaPills/SlaPills';
import { Route } from '../../../../route-constants';
import {  i18n, openGroupChat } from '../../utils/helper';
import { DateTime } from 'luxon';
import i18next from 'i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { truncateString } from '../../../tikit/MarkdownHelper';
import { AsyncCircleImage } from '../CircleImages';
import { ICustomProperty } from "../../interfaces/customproperties.interface";
import { CustomPropertyResting } from "./CustomPropertyResting";
import { PillList } from "./PillList";
import { DurationText } from "../TicketTimeTracking/DurationText";
import { COLUMN_DRAG_FLOOR, LIST_PREFERENCES_CHANGED, ListPreferencesChangedDetail, applyColumnOrder, applyColumnWidths, applyHiddenColumns, hideColumn, moveColumn, readColumnOrder, readColumnWidths, readHiddenColumns, saveColumnWidth, writeListPreference } from "./listPreferences";
import { headerKeyedByColumnOrder, columnPointerDrag, markSortableColumns, seedAutoFitWidths } from "./columnDrag";
import { InlinePickerCell, PickerField } from "./InlinePickerCell";
import { InlineRequesterCell } from "./InlineRequesterCell";
import { InlineStatusCell } from "./InlineStatusCell";
import { AssignmentColumn, InlineAssignmentCell } from "./InlineAssignmentCell";
import { InlineTagsCell, TagPills, TagRecord } from "./InlineTagsCell";
import { ticketSaveGuard } from "./ticketSaveGuard";
import { InlineCustomPropertyCell, InlineDateCell, committing } from "./InlineFieldCells";
import { platformService } from "../../services/platform.service";
import { FormQuestion, changedFormColumns, formAnswerChoices, formAnswerText, isCustomPropertyColumnKey } from "../../../tikit/List/formColumns";
import { FormAnswersDialog, SavedForm } from "../../../tikit/List/FormAnswersDialog";
import { FormQuestionCell } from "./FormQuestionCell";
import { finishFormSave, setFormsOpen, startFormSave } from "../../../tikit/List/formSaves";

const { t } = i18n();

export interface State {
  columns: IColumn[];
  items: Ticket[];
  selectedItems: Ticket[];
  analystAccess: boolean;
  layoutVersion: number;
  /** Tickets a save in a cell is taking out of the view, by Id (rowLeave.ts). */
  leaving: LeavingRows<number>;
  /** The ticket whose forms are open from a form question cell, and that cell's column. */
  formsTicketId?: number;
  formsColumnKey?: number;
}

type PropsType = RouteComponentProps<{}> & {
  tickets: Ticket[];
  onTicketsSelected: (tickets: Ticket[]) => void;
  currentState: AppStateValue;
  useTicketType: boolean;
  useTeam: boolean;
  useTicketLifecycle: boolean;
  useSLA: boolean;
  useResolutionNote: boolean;
  useGroupChat: boolean;
  useCustomProperties: boolean;
  useTimeTracking: boolean;
  /** Ticket Tags is licensed: the Tags column shows. */
  useTicketTags: boolean;
  /** Ticket Templates is licensed: the Template column shows. */
  useTicketTemplates?: boolean;
  /** The view's custom form question columns (TicketListBoard), drawn after the rest. Not sortable yet. */
  formColumns?: FormQuestion[];
  setListStates?: React.Dispatch<React.SetStateAction<ListStatesType>>;
  selectedOrderedColumns: null | (number | string)[];
  sortOrderApplied: {
    orderBy: ISortOrderBy;
    orderType: ISortOrderType;
  };
  clickColumnCallback: (x: IColumn) => void;
  customViewStates?: CustomViewStatesType;
  /**
   * Rendered outside the ticket list, as the user page's Tickets tab is: no row selection (there is
   * no bulk edit to drive), selectedOrderedColumns applies from the first render rather than only
   * when it changes, and ticket links open in a new browser tab so the page stays where it is.
   */
  standalone?: boolean;
  columnWidthsKey: string;
  columnOrderKey: string;
  /** Where the columns the agent removed with a header's X are kept; without it no header has an X. */
  hiddenColumnsKey?: string;
  /** Called when the column removed is the one the list is sorted by, so the list can go back to its default sort. */
  onSortedColumnRemoved?: () => void;
  /** Set where a cell can be edited in place; asks the list for the page again after a save. */
  onTicketEdited?: () => void | Promise<void>;
  /** What choosing Resolved asks for in an editable Status cell, when resolution notes are on. */
  resolutionNoteConfig?: TikitConfiguration;
};

const EDIT_LISTS: (keyof AppStateValue)[] = [
  'teams', 'supportgroups', 'usersInRole', 'tags', 'taskStatus', 'ticketStatus', 'categories', 'priority', 'ticketTypes'
];

class ClassDetailsList extends React.Component<PropsType, State> {
  private _selection: Selection;

  constructor(props: PropsType) {
    super(props);

    this._selection = new Selection({
      onSelectionChanged: () => {
        this.setState({
          selectedItems: this._getSelectionDetails(),
        });
      },
    });

    this.props.setListStates?.((prev) => ({
      ...prev,
      rowsSelection: this._selection,
    }));


    let cols = this._allColumns();
    cols = this.filterColumnsByFeature(cols, this.props);

    if (this.props.standalone)
      cols = this.getOrderedColumns(cols);
    else if(!this.isViewEdited() && (this.props.selectedOrderedColumns?.length ?? 0) == 0)
      cols = cols.filter(c =>  Number(c.key) < 1000);
    this.state = {
      items: this.props.tickets,
      columns: this.withSavedLayout(cols),
      selectedItems: [],
      analystAccess: false,
      layoutVersion: 0,
      leaving: new Map(),
    };
  }

  isViewEdited = () => this.props.customViewStates?.selectedCustomView != undefined;

  getLayoutMode = () => {
    return (localStorage.getItem("TikitViewsHorizontalScroll") ?? "true") === "true"
      ? DetailsListLayoutMode.fixedColumns
      : DetailsListLayoutMode.justified;
  };

  // Only the fixed-columns layout takes a starting width from the columns passed in. Justified (auto-fit)
  // starts every column at its minWidth, so there the minWidth drops to the drag floor and each column's
  // default or saved width goes in through Fluent's record of dragged widths instead (_seedAutoFit).
  withSavedLayout = (cols: IColumn[]) => {
    const shown = this.props.hiddenColumnsKey ? applyHiddenColumns(cols, readHiddenColumns(this.props.hiddenColumnsKey)) : cols;
    const ordered = applyColumnOrder(shown, readColumnOrder(this.props.columnOrderKey));
    return this.getLayoutMode() === DetailsListLayoutMode.fixedColumns
      ? applyColumnWidths(ordered, readColumnWidths(this.props.columnWidthsKey))
      : ordered.map(column => ({ ...column, minWidth: Math.min(column.minWidth, COLUMN_DRAG_FLOOR) }));
  };

  private _detailsList: unknown = null;
  private _rootRef = React.createRef<HTMLDivElement>();
  /** Draws the tickets as they now are, fading out the ones a save in a cell took out of the view (rowLeave.ts). */
  private _rows = rowLeaver<Ticket, number>(ticket => ticket?.Id, {
    shown: () => this.state.items,
    update: next => this.setState(prev => next(prev)),
  });

  /**
   * Puts a saved value into its row before the reload arrives (measured 1.4s on a full page, against 0.2s for the
   * save), so a panel cell can draw it and go green at once. The reload then replaces the row with what the server
   * holds.
   */
  _patchRow = (ticketId: number, patch: Partial<TicketProp>) =>
    this.setState(prev => ({ items: prev.items.map(row => (row.Id === ticketId ? { ...row, ...patch } as typeof row : row)) }));

  _onTicketEdited = () => this._rows.track(() => this.props.onTicketEdited?.());

  /** Shows the tickets as they now are - after fading any that left, when a save in a cell caused it. */
  private _showTickets() {
    this._rows.show(this.props.tickets);
  }

  private _onRenderRow: IRenderFunction<IDetailsRowProps> = (props, defaultRender) => {
    const phase = props && this.state.leaving.get((props.item as Ticket)?.Id);
    return defaultRender(phase ? { ...props, className: `${props.className ?? ''} ${leaveClass(phase)}` } : props);
  };

  private _seedAutoFit = (columns: IColumn[]) =>
    this.getLayoutMode() === DetailsListLayoutMode.justified &&
    seedAutoFitWidths(this._detailsList, columns, readColumnWidths(this.props.columnWidthsKey));

  // Fluent reports a resize the moment a divider is pressed, unmoved, so only a real change is kept.
  // `column` is Fluent's laid-out column, so its calculatedWidth is the width before this move.
  private _onColumnResize = (column: IColumn, newWidth: number) => {
    if (column.calculatedWidth != null && Math.round(column.calculatedWidth) === Math.round(newWidth)) return;
    saveColumnWidth(this.props.columnWidthsKey, column.key, newWidth);
  };

  // Each mounted list gets its widths seeded, then a fresh columns array makes Fluent lay out again with
  // them - inside the same commit, so the floor-width first layout is never painted.
  private _onDetailsListRef = (detailsList: unknown) => {
    this._detailsList = detailsList;
    if (detailsList && this._seedAutoFit(this.state.columns))
      this.setState(prev => ({ columns: [...prev.columns] }));
  };

  private _columnPointerDrag = columnPointerDrag((draggedIndex, targetIndex) =>
    this._columnReorderOptions.onColumnDrop({ draggedIndex, targetIndex }));

  private _columnReorderOptions = {
    onColumnDrop: ({ draggedIndex, targetIndex }: { draggedIndex: number; targetIndex: number }) => {
      const columns = moveColumn(this.state.columns, draggedIndex, targetIndex);
      writeListPreference(this.props.columnOrderKey, columns.map(column => column.key));
      this.setState({ columns });
    },
  };

  // The X on a header: the column is hidden for this agent on this view until Reset View. The rest keep their
  // widths and order; a list sorted by the removed column goes back to its default sort.
  private _onRemoveColumn = (columnKey: string) => {
    if (!this.props.hiddenColumnsKey) return;
    hideColumn(this.props.hiddenColumnsKey, columnKey);
    this.setState(prev => ({ columns: prev.columns.filter(column => column.key !== columnKey) }));
    if (Number(columnKey) === Number(this.props.sortOrderApplied?.orderBy)) this.props.onSortedColumnRemoved?.();
  };

  // The header menu's actions. Its sort goes through the same callback as a header click, which flips the column's
  // current direction - so it is handed the opposite of the direction wanted.
  private get _headerActions() {
    return {
      onSortColumn: (column: IColumn, descending: boolean) => this.props.clickColumnCallback({ ...column, isSortedDescending: !descending }),
      onRemoveColumn: this.props.hiddenColumnsKey ? this._onRemoveColumn : undefined,
    };
  }

  public render() {
    const { columns, items } = this.state;

    return (
      <FluentUIThemeConsumer
        render={(globalTheme) => (
          <div
            ref={this._rootRef}
            className={`py-3.5 px-2.5 min-h-32 main-ticket-list frozen-list-header${this.props.standalone ? ' standalone-ticket-list' : ''}`}
            style={{
              backgroundColor: this.props.standalone ? "transparent" : "var(--mgt-theme-background2)",
              ...ThemeColorScheme(globalTheme.siteVariables),
            }}
          >
            <div {...this._columnPointerDrag}>
              <MarqueeSelection selection={this._selection}>
                <DetailsList
                  selectionZoneProps={NO_SELECT_ON_FOCUS}
                  // Fluent keeps every width it has laid out and lets it outrank the columns
                  // passed in, so a different view's saved widths only apply on a fresh list.
                  key={`${this.props.columnWidthsKey}#${this.state.layoutVersion}`}
                  onColumnResize={this._onColumnResize}
                  componentRef={this._onDetailsListRef}
                  columnReorderOptions={this._columnReorderOptions}
                  onRenderDetailsHeader={headerKeyedByColumnOrder(columns, this._headerActions)}
                  items={items}
                  onRenderRow={this._onRenderRow}
                  compact={false}
                  columns={columns}
                  selectionMode={
                    this.props.standalone ||
                    isCloseFilter(this.props.currentState.listFilter) ||
                    !this.state.analystAccess
                      ? SelectionMode.none
                      : SelectionMode.multiple
                  }
                  getKey={this._getKey}
                  setKey="multiple"
                  layoutMode={this.getLayoutMode()}
                  isHeaderVisible={true}
                  selection={this._selection}
                  selectionPreservedOnEmptyClick={true}
                  enterModalSelectionOnTouch={true}
                  ariaLabelForSelectionColumn="Toggle selection"
                  ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                  checkButtonAriaLabel="select row"
                  className="details-list-table"
                  // At most one page of rows (100), and the grid marks itself as the element it scrolls in, so inside
                  // another scrolling box it never heard the scroll and left the last rows as blank space.
                  onShouldVirtualize={this.props.standalone ? () => false : undefined}
                />
              </MarqueeSelection>
            </div>
            {!!this.state.formsTicketId && (
              <FormAnswersDialog
                ticketId={this.state.formsTicketId}
                onClose={this._closeForms}
                onSaving={this._formsSaving(this.state.formsTicketId, this.state.formsColumnKey)}
                onSaved={this._formsSaved(this.state.formsTicketId)}
                onFailed={this._formsFailed(this.state.formsTicketId)}
              />
            )}
          </div>
        )}
      />
    );
  }

  // Reset view clears this view's widths and order; rebuild from the view's own, and re-mount the list
  // so Fluent drops the widths it was holding.
  private _onListPreferencesChanged = (event: Event) => {
    const { storageKey, cleared } = (event as CustomEvent<ListPreferencesChangedDetail>).detail;
    const layoutKeys = [this.props.columnWidthsKey, this.props.columnOrderKey, this.props.hiddenColumnsKey];
    if (!cleared || !layoutKeys.includes(storageKey)) return;
    const cols = this.filterColumnsByFeature(this._allColumns(), this.props);
    this.setState(prev => ({ columns: this.withSavedLayout(this.getOrderedColumns(cols)), layoutVersion: prev.layoutVersion + 1 }));
  };

  public componentWillUnmount() {
    window.removeEventListener(LIST_PREFERENCES_CHANGED, this._onListPreferencesChanged);
    this._rows.stop();
  }

  /**
   * A standalone grid is transparent, on whatever surface holds it (the grey user page, the white side panel on a
   * ticket), so its frozen header has no colour of its own to cover the rows with - it takes that surface's, the
   * first ancestor with a background, through --frozen-header-bg (main.css). Read again on every update, so a
   * theme change follows.
   */
  private _matchHeaderToSurface() {
    const root = this._rootRef.current;
    if (!this.props.standalone || !root) return;
    let surface = 'transparent';
    for (let element = root.parentElement; element; element = element.parentElement) {
      const color = getComputedStyle(element).backgroundColor;
      if (color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)') { surface = color; break; }
    }
    if (root.style.getPropertyValue('--frozen-header-bg') !== surface) root.style.setProperty('--frozen-header-bg', surface);
  }

  public componentDidMount() {
    this._matchHeaderToSurface();
    window.addEventListener(LIST_PREFERENCES_CHANGED, this._onListPreferencesChanged);
    (async () => {
      let isAnalystAccess = await getCachedFeature(
        PRODUCT_LICENSE.AnalystAccess
      );
      // A new array, so Fluent draws the rows again: they may already be drawn without the editable cells, and
      // it re-renders a row only when the items change (seen on the user page's Tickets tab).
      this.setState({
        items: [...this.props.tickets],
        analystAccess: isAnalystAccess,
      });
    })();
  }

  public filterColumnsByFeature(cols: IColumn[], props: PropsType) {
    let excludeCols = [];
    if (!props.useTicketType) excludeCols.push("TicketType");
    if (!props.useTeam) excludeCols.push("TeamName");
    if (!props.useTicketLifecycle) {
      excludeCols.push("TicketLifecycleName");
      excludeCols.push("TicketLifecyclePhaseName");
    }
    if (!props.useSLA) excludeCols.push("SLA");

    if (!props.useResolutionNote){
      excludeCols.push("InternalNote");
      excludeCols.push("ResolutionCategory");
      excludeCols.push("Note");
      excludeCols.push("DraftKB");
    }

    if (!props.useGroupChat) {
      excludeCols.push("GroupChat");
    }

    // The column picker gates this too (CustomViewPopup/Content.tsx), but that only stops
    // it being *chosen*. Without this the grid still draws it - as the whole built-in set
    // when no view is selected, and inside any view saved before the feature was turned
    // off - giving an unlicensed tenant a Total Agent Time column reading 0m on every row.
    if (!props.useTimeTracking) {
      excludeCols.push("TotalTimeSpentMinutes");
    }
    if (!props.useTicketTags) {
      excludeCols.push("Tags");
    }
    if (!props.useTicketTemplates) {
      excludeCols.push("TemplateName");
    }
    if(excludeCols.length > 0)
      cols = cols.filter(x => !excludeCols.includes(x.fieldName));

    if (!props.useCustomProperties) 
      cols = cols.filter(c => !isCustomPropertyColumnKey(c.key));

    return cols;
  }

  public getOrderedColumns(cols: IColumn[]) {
    const selectedOrderedColumns = [...this.props.selectedOrderedColumns];
    if (selectedOrderedColumns?.length > 0) {
      const filteredCols = cols.filter((col) =>
        selectedOrderedColumns.includes(Number(col.key))
      );

      const orderedColumns = selectedOrderedColumns.reduce((acc, value) => {
        const matchedColumn = filteredCols.find(
          (column) => Number(column.key) === value
        );
        return [...acc, matchedColumn];
      }, [] as IColumn[]);
      return orderedColumns.filter((item) => item);
    }

    if(!this.isViewEdited() && (this.props.selectedOrderedColumns?.length ?? 0) == 0)
      cols = cols.filter(c =>  Number(c.key) < 1000);

    return cols;
  }

  public componentDidUpdate(previousProps: PropsType, previousState: State) {
    this._matchHeaderToSurface();
    // Compared row by row and on Category too: a save made in a cell comes back as the same tickets with
    // one value changed, or in a new order when the list is sorted by it, and neither changes a Title.
    if (
      (this._showNextTickets && previousProps.tickets !== this.props.tickets) ||
      previousProps.tickets.length !== this.props.tickets.length ||
      previousProps.tickets.some((ticket, i) => rowChanged(ticket, this.props.tickets[i]))
    ) {
      this._showNextTickets = false;
      this._showTickets();
    }

    // Fluent re-renders a row only when its item changes, so a list the edit cells choose from that arrives after
    // the rows are drawn - the user page loads them late, and the ticket list's teams gain their members from
    // Graph afterwards - would never reach a cell. A new array draws the rows again.
    if (EDIT_LISTS.some(list => previousProps.currentState?.[list] !== this.props.currentState?.[list])) {
      this.setState(prev => ({ items: [...prev.items] }));
    }

    if (
      previousProps.tickets.every((x) => !x.TicketSlas) &&
      this.props.tickets.some((x) => x.TicketSlas?.length > 0)
    ) {
      this._showTickets();
    }

    if (
      JSON.stringify(previousProps.selectedOrderedColumns) !==
      JSON.stringify(this.props.selectedOrderedColumns) ||
      previousProps.columnWidthsKey !== this.props.columnWidthsKey ||
      previousProps.columnOrderKey !== this.props.columnOrderKey ||
      previousProps.hiddenColumnsKey !== this.props.hiddenColumnsKey ||
      formColumnKeys(previousProps.formColumns) !== formColumnKeys(this.props.formColumns)
    ) {
      let cols = this._allColumns();
      cols = this.filterColumnsByFeature(cols, this.props);

      const columns = this.withSavedLayout(this.getOrderedColumns(cols));
      // A column the list did not have before must get its default before Fluent lays it out at the floor.
      this._seedAutoFit(columns);
      this.setState({ columns });
    }

    // The arrows follow the sort without re-mounting the list: Reset View fetches again with the rows left on
    // screen, so nothing else rebuilds the columns, which take their arrows when the list is built.
    const { orderBy, orderType } = this.props.sortOrderApplied;
    if (previousProps.sortOrderApplied.orderBy !== orderBy || previousProps.sortOrderApplied.orderType !== orderType) {
      const withArrow = (column: IColumn): IColumn => {
        const isSorted = Number(column.key) === Number(orderBy);
        return { ...column, isSorted: isSorted ? true : null, isSortedDescending: isSorted && orderType === EnumSortOrderType.desc };
      };
      this._columns = this._columns.map(withArrow);
      this.setState(prev => ({ columns: prev.columns.map(withArrow) }));
    }
  }

  /** A form question cell opens its ticket's forms (FormAnswersDialog); the cell's row takes the editing tint. */
  _openForms = (ticketId: number, columnKey: number) => {
    setFormsOpen({ ticketId, columnKey });
    this.setState({ formsTicketId: ticketId, formsColumnKey: columnKey });
  };

  private _closeForms = () => {
    setFormsOpen(null);
    this.setState({ formsTicketId: undefined, formsColumnKey: undefined });
  };

  /**
   * An answer-now save or a Send from the dialog, shown as the cells show their own saves: any new answers go into the
   * row at once, and the cells dim and pulse, then turn green or red (Shaun, 2026-10-14/15). A Send marks every form
   * question cell in the row; a save marks the cells whose answer changed - or, when none of them is a column of the
   * view, the cell it was opened from, so every save shows its outcome somewhere (as the Team / Group / Assignee panel).
   */
  private _formsSaving = (ticketId: number, openedFrom: number) => (forms: SavedForm[]) => {
    const row = this.state.items.find(item => item.Id === ticketId);
    const before = row?.CustomForms ?? [];
    const after = [...before.filter(form => !forms.some(saved => saved.TemplateFormId === form.TemplateFormId)), ...forms];
    // The view's questions as they are on screen, in their order - not those the agent removed from it with a header's
    // X, whose cells are not drawn: an answer changed only there marks the cell it was opened from (Shaun).
    const formColumns = this.props.formColumns ?? [];
    const questions = this.state.columns
      .map(column => formColumns.find(question => String(question.key) === column.key))
      .filter((question): question is FormQuestion => !!question);
    startFormSave(ticketId, forms.length === 0 ? questions.map(question => question.key) : changedFormColumns(questions, before, after, openedFrom), openedFrom);
    this._formsBefore.set(ticketId, row?.CustomForms);
    this._patchRow(ticketId, { CustomForms: after });
  };

  /** A row's answers as they were before a dialog save put the new ones in, to put back if it fails. */
  private _formsBefore = new Map<number, TicketProp['CustomForms']>();

  private _formsSaved = (ticketId: number) => () => {
    this._formsBefore.delete(ticketId);
    finishFormSave(ticketId, 'saved');
    this._onTicketEdited();
  };

  /**
   * The answers put into the row go back at once, and the reload's rows are taken whether or not they differ from the
   * last: a failed answer save leaves the ticket's ModifiedDate where it was, so rowChanged would keep the patched row.
   * The reload also shows answers that did land when only the form's files failed after them.
   */
  private _formsFailed = (ticketId: number) => (error: unknown) => {
    finishFormSave(ticketId, 'failed');
    reportFailure(error);
    if (this._formsBefore.has(ticketId)) this._patchRow(ticketId, { CustomForms: this._formsBefore.get(ticketId) });
    this._formsBefore.delete(ticketId);
    this._showNextTickets = true;
    this._onTicketEdited();
  };

  /** Set by a failed form save: the next tickets from the list are shown even if no row looks changed. */
  private _showNextTickets = false;

  private _allColumns = (): IColumn[] => [...this._columns, ...(this.props.formColumns ?? []).map(question => formColumn(question, this))];

  /**
   * Rows keyed by ticket, so a row and its cells' state (a save's green box, the value being saved) move with their
   * ticket when a row above leaves; keyed by position, they stayed put and showed on the ticket that moved up. The
   * row Add Ticket puts in has no Id yet.
   */
  private _getKey(item: Ticket, index?: number): string {
    return item?.Id != null ? `ticket-${item.Id}` : `row-${index}`;
  }

  private _getSelectionDetails(): Ticket[] {
    this.props.onTicketsSelected(this._selection.getSelection() as Ticket[]);
    return this._selection.getSelection() as Ticket[];
  }

  _onColumnClick = (
    ev: React.MouseEvent<HTMLElement>,
    column: IColumn
  ): void => {
    this.props.clickColumnCallback(column);
  };

  private _requestLength =
    (localStorage.getItem("TikitRequestLength") || "abbreviated") == "full";

  private _cols: _ColsType = {
    Id: [ListColumnKey.Id, t('ticket.ticket-list.id'), 'Id', 30, 30, null, true, renderRecordValue('Id', this)],
    Type: [ListColumnKey.Type, t('ticket.ticket-list.type'), 'TicketType', 100, 100, null, null, renderRecordValue('Type', this)],
    Request: [ListColumnKey.Title, t('ticket.ticket-list.request'), 'Title', (this._requestLength ? 100 : 200), (this._requestLength ? 200 : null), (this._requestLength ? true : null), true, renderRecordValue('Request', this)],
    Status: [ListColumnKey.Status, t('ticket.ticket-list.status'), 'Status', 125, 125, null, null, renderRecordValue('Status', this)],
    GroupChat: [ListColumnKey.GroupChat, t('ticket.ticket-list.group-chat'), 'GroupChat', 100, 100, null, null, renderRecordValue('GroupChat', this)],
    Requester: [ListColumnKey.Requester, t('ticket.ticket-list.requester'), 'RequesterName', 150, 150, null, null, renderRecordValue('Requester', this)],
    Category: [ListColumnKey.Category, t('ticket.ticket-list.category'), 'Category', 125, 125, null, null, renderRecordValue('Category', this)],
    Priority: [ListColumnKey.Priority, t('ticket.ticket-list.priority'), 'Priority', 100, 100, null, null, renderRecordValue('Priority', this)],
    SLAs: [ListColumnKey.SLA, t('ticket.ticket-list.sla'), 'SLA', 100, 100, null, null, renderRecordValue('SLAs', this)],
    Team: [ListColumnKey.Team, t('ticket.ticket-list.team'), 'TeamName', 100, 100, null, null, renderRecordValue('Team', this)],
    Group: [ListColumnKey.Group, t('ticket.ticket-list.group'), 'GroupName', 100, 100, null, null, renderRecordValue('Group', this)],
    Assignee: [ListColumnKey.Assignee, t('ticket.ticket-list.assignee'), 'AssigneeName', 100, 100, null, true, renderRecordValue('Assignee', this)],
    // After Assignee, as the edit-ticket page lists them (Shaun, 2026-10-13).
    Tags: [ListColumnKey.Tags, t('ticket.add-ticket.tag'), 'Tags', 120, 200, null, null, renderRecordValue('Tags', this)],
    // Between Tags and Lifecycle (Shaun, 2026-10-14); read-only - changing a template is the apply-template flow.
    Template: [ListColumnKey.Template, t('ticket-details.edit-ticket-view.template'), 'TemplateName', 100, 150, null, null, renderRecordValue('Template', this)],
    // 20% wider than most (Shaun, 2026-10-23): lifecycle and phase names are long.
    Lifecycle: [ListColumnKey.Lifecycle, t('ticket.ticket-list.lifecycle'), 'TicketLifecycleName', 120, 120, null, null, renderRecordValue('Lifecycle', this)],
    Phase: [ListColumnKey.Phase, t('ticket.ticket-list.phase'), 'TicketLifecyclePhaseName', 120, 120, null, null, renderRecordValue('Phase', this)],
    // From here: Total Agent Time, Source, Due, Last Modified, Created, Resolution Date, then resolution (Shaun, 2026-10-23).
    // Every view that has not chosen its own columns - the five out-of-the-box views among them - uses this order,
    // and the view editor's picker (DASHBOARD_TABLE_COLUMNS) lists them the same way.
    // Wider than the default 100 only so the header is not truncated - "Total Agent
    // Time" is a longer label than most, and several translations are longer still.
    TotalAgentTime: [ListColumnKey.TotalAgentTime, t('ticket.ticket-list.total-agent-time'), 'TotalTimeSpentMinutes', 120, 138, null, null, renderRecordValue('TotalAgentTime', this)],
    Source: [ListColumnKey.Source, t('automation.automation-properties.source'), 'SourceName', 100, 140, null, null, renderRecordValue('Source', this)],
    'Due Date': [ListColumnKey.DueDate, t('ticket.ticket-list.due-date'), 'DueDate', 100, 100, null, null, renderRecordValue('Due Date', this)],
    'Last Modified Date': [ListColumnKey.LastModifiedDate, t('ticket.ticket-list.last-modified-date'), 'ModifiedDate', 100, 100, null, null, renderRecordValue('Last Modified Date', this)],
    'Created Date': [ListColumnKey.CreatedDate, t('ticket.ticket-list.created-date'), 'CreatedDate', 100, 100, null, null, renderRecordValue('Created Date', this)],
    'Resolution Date': [ListColumnKey.ResolutionDate, t('ticket.ticket-list.resolution-date'), 'ResolutionDate', 100, 100, null, null, renderRecordValue('Resolution Date', this)],
    ResolutionCategory : [ListColumnKey.ResolutionCategory, t('resolution-note.resolution-category'), 'ResolutionCategory', 100, 100, null, null, renderRecordValue('ResolutionCategory', this)],
    Note: [ListColumnKey.Note, t('resolution-note.resolution-note'), 'Note', (this._requestLength ? 100 : 200), (this._requestLength ? 100 : 200), (this._requestLength ? true : null), true, renderRecordValue('Note', this)],
    InternalNote: [ListColumnKey.InternalNote, t('resolution-note.resolution-note-internal'), 'InternalNote', (this._requestLength ? 100 : 200), (this._requestLength ? 100 : 200), (this._requestLength ? true : null), true, renderRecordValue('InternalNote', this)],
    DraftKB: [ListColumnKey.DraftKB, t('resolution-note.draft-kb'), 'DraftKB', 100, 100, null, null, renderRecordValue('DraftKB', this)]
  };

  private _colsWithProps = () => {
    if(this.props.useCustomProperties){
      let customPropertyColId = 1000;
      this.props.currentState?.customProperties.filter(p => !p.IsHidden).sort((a, b) => a.Order - b.Order).forEach(cp => {
        this._cols[cp.PropertyId] = [customPropertyColId + cp.Id, cp.Name, cp.PropertyId, 100, 100, null, null, renderCPRecordValue(cp, this)];
      });
    }
    return this._cols;
  }

  private _columns: IColumn[] = markSortableColumns(Object.values(this._colsWithProps()).map((value): IColumn => {
    const orderBy = this.props.sortOrderApplied.orderBy;
    const orderType = this.props.sortOrderApplied.orderType;

    return {
      key: value[0] + "",
      name: value[1],
      fieldName: value[2],
      minWidth: value[3],
      maxWidth: value[4],
      isMultiline: value[5],
      isPadded: value[6],
      onRender: value[7],
      isSorted: value[0] === orderBy ? true : null,
      isSortedDescending: value[0] === orderBy && orderType === EnumSortOrderType.desc,
      onColumnClick: this._onColumnClick,
      isResizable: true,
      isCollapsible: true,
    };
  }), column => !NON_SORTABLE_COLUMNS.includes(Number(column.key)));
}

export default withRouter(ClassDetailsList);

/** What the cell helpers below read from the list - the list itself, which they are handed as `this`. */
type CellContext = Pick<ClassDetailsList, 'props' | 'state' | '_patchRow' | '_onTicketEdited' | '_openForms' | '_onColumnClick'>;

const formColumnKeys = (questions?: FormQuestion[]) => (questions ?? []).map(question => question.key).join(',');

const formColumn = (question: FormQuestion, thisContext: CellContext): IColumn => ({
  key: String(question.key),
  name: question.label,
  fieldName: `form:${question.key}`,
  minWidth: 100,
  maxWidth: 200,
  isResizable: true,
  isCollapsible: true,
  // Sorted on the server by the answer (formorderby - FormQuestionExtensions), as custom properties are.
  headerClassName: 'is-sortable',
  onColumnClick: thisContext._onColumnClick,
  isSorted: Number(thisContext.props.sortOrderApplied?.orderBy) === question.key ? true : null,
  isSortedDescending: Number(thisContext.props.sortOrderApplied?.orderBy) === question.key && thisContext.props.sortOrderApplied?.orderType === EnumSortOrderType.desc,
  onRender: (item: TicketProp) =>
    item.Title === 'temp_ticket' ? <SkeletonCell /> : cachedCell(item, thisContext, `form:${question.key}`, () => drawFormCell(question, thisContext, item)),
});

const drawFormCell = (question: FormQuestion, thisContext: CellContext, item: TicketProp) => {
    const text = formAnswerText(question, item.CustomForms, answerTextOptions());
    const pills = question.multiSelect ? formAnswerChoices(question, item.CustomForms) : undefined;
    if (!isInlineEditable(item, thisContext))
      return pills
        ? <PillList names={pills} title={question.label} />
        : <div className='w-full truncate' title={text || undefined}><bdi>{text || EMPTY_CELL}</bdi></div>;
    // The ticket page's form dialog, over the list: send the form, or answer it now (FormAnswersDialog).
    return (
      <FormQuestionCell
        ticketId={item.Id}
        columnKey={question.key}
        label={question.label}
        text={text}
        pills={pills}
        onOpen={() => thisContext._openForms(item.Id, question.key)}
      />
    );
};

const answerTextOptions = () => ({
  trueText: t('ticket.child-filter.true'),
  falseText: t('ticket.child-filter.false'),
  locale: navigator.language ?? i18next.language,
});

type ColValuesType = [
  ISortOrderBy,
  string,
  string,
  number,
  number,
  boolean | null,
  boolean | null,
  (item: TicketProp) => JSX.Element
];
type _ColsType = {
  [x in TicketColumnNameType]: ColValuesType;
};

interface ILinkRecord {
  ticketId: number;
  itemId?: number;
  value: number | string;
  title: string;
  type?: number;
  newTab?: boolean;
}
export const LinkRecord = ({
  ticketId: ticId,
  itemId,
  value,
  title,
  type,
  newTab,
}: ILinkRecord) => {
  const location = useLocation();
  const ticketId = ticId.toString();
  const setAppState = useSetState();

  const isTaskOrApproval = [
    MyWorkListViewType.Approvals,
    MyWorkListViewType.Tasks,
  ].includes(type);
  const onClickLink = () => {
    if (isTaskOrApproval) {
      setAppState((prev) => ({
        ...prev,
        isRouteFromMyWork: true,
        selectedTaskOrApproval: { type, id: itemId },
      }));
    }
  };

  const isMyGroupWorkListView = location.pathname === Route.myGroupWork.list;
  const isMyWorkListView = location.pathname === Route.myWork.list;
  let path = "";
  let from = "";

  if (isMyGroupWorkListView || isMyWorkListView) {
    const route = isMyGroupWorkListView
      ? Route.myGroupWork.editTicket
      : Route.myWork.editTicket;
    path = `${route}/${ticketId}`;
    from = isMyGroupWorkListView ? "mygroupwork" : "mywork";
  } else {
    path = `/tickets/${ticketId}`;
    from = "";
  }

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <Tooltip
          align={"center"}
          position={"below"}
          trigger={<span
                className='class-details-grid-span'
                style={{
                  cursor: 'pointer',
                  color: globalTheme.siteVariables.colorScheme.brand.foregroundActive,
                }}
              >
            {newTab
              ? <Link to={path} target="_blank" rel="noopener noreferrer">{value}</Link>
              : <Link to={{pathname: path, state: { from }}} onClick={onClickLink}>{value}</Link>}
          </span>}
          content={title}
        />
      )}
    />
  );
};

export const SLAPills = (ticketSlas) => {
  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <SlaPills
          ticketSlas={ticketSlas.ticketSlas}
          svgGap={false}
          viewType="List"
          fitToCell
        />
      )}
    />
  );
};

const _getRequester = (item: TicketProp, thisContext: any) => {
  const user = item.Requester;
  const affectedUsers = item.AffectedUsers;

  if (affectedUsers.length > 0) {
    const users = user ? [{ PlatformUserId: user.Id, PlatformUser: user }, ...affectedUsers] : affectedUsers;
    return _getUsers(users);
  }

  return _getUser(user?.AadObjectId ? user?.AadObjectId : user?.UserName, user?.FullName);
};

const _getAssignee = (item: TicketProp, thisContext: any) => {
  const user = item.Assignee;
  const collaborators = item.TicketCollaborators;

  if (collaborators.length > 0) {
    const users = user ? [{ PlatformUserId: user.Id, PlatformUser: user }, ...collaborators] : collaborators;
    return _getUsers(users, t('ticket.ticket-list.collaborators'));
  }

  return _getUser(user?.AadObjectId ? user?.AadObjectId : user?.UserName, user?.FullName);
};


export const MarkdownTooltip = (prop) =>{
  let detail = prop?.details ?? "";
  let cssClass = prop.context._requestLength ? "class-details-grid-span" : "class-details-grid-span-abbreviated";
  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <Tooltip
        align={"center"}
        position={"below"}
        trigger={<span className={cssClass}>
          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents} >
            {truncateString(detail, 350)}
          </Markdown>
          </span>}
        content={<span>
          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents} >
            {detail}
          </Markdown>
          </span>}
      />
      )}
    />
  );
}


/** `path` is the status with its parents ("Pending > Pending Employee") - its tooltip, as every tiered cell's is. */
export const StatusPillCell = ({ status, path }: { status: TicketProps; path?: string }) => (
  <div className="status-pill-cell" {...hoverTitle(path || status?.Value, !!path && path !== status?.Value)}>
    <TicketStatus status={status} postion="start" />
  </div>
);

const renderRecord = (
  item: TicketProp,
  thisContext: any
): { [x in TicketColumnNameType]: React.ReactElement } => ({
  Id: <LinkRecord ticketId={item.Id} value={item.Id} title={item.Title} newTab={thisContext.props.standalone} />,
  Request: (
    <LinkRecord ticketId={item.Id} value={item.Title} title={item.Title} newTab={thisContext.props.standalone} />
  ),
  Assignee: assignmentCell(item, thisContext, _getAssignee(item, thisContext), 'assignee'),
  Status: isStatusEditable(item, thisContext) ? (
    <InlineStatusCell
      ticket={item}
      statuses={thisContext.props.currentState.ticketStatus}
      taskStatuses={thisContext.props.currentState.taskStatus}
      lifecycleEnabled={thisContext.props.useTicketLifecycle}
      resolutionNotesOn={thisContext.props.useResolutionNote && !!thisContext.props.resolutionNoteConfig}
      resolutionNoteConfig={thisContext.props.resolutionNoteConfig}
      renderPill={(statusId: number | null) => statusPill(statusId, item, thisContext)}
      onSaved={thisContext._onTicketEdited}
      guard={ticketSaveGuard(item.Id, true, thisContext.props.currentState.customProperties)}
    />
  ) : statusPill(item.StatusId, item, thisContext),
  GroupChat: (
    <>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {item.GroupChat?.Members && item.GroupChat.Members.length > 0 && (
        <AsyncCircleImage members={item.GroupChat.Members} />
      )}
      {item.GroupChatId ? (
        <a href="#" onClick={() => openGroupChat(item.GroupChat.ConversationId)}>
          {t('ticket.ticket-board.ticket-card.open-chat')}
        </a>
      ) : (
        <span>{t('ticket.ticket-board.ticket-card.no-chat')}</span>
      )}
    </div>
    </>
  ),
  Requester: isInlineEditable(item, thisContext) ? (
    <InlineRequesterCell
      ticketId={item.Id}
      requester={item.Requester}
      affectedUsers={item.AffectedUsers ?? []}
      resting={_getRequester(item, thisContext)}
      onSaved={thisContext._onTicketEdited}
      onPatch={(patch: Partial<TicketProp>) => thisContext._patchRow(item.Id, patch)}
      guard={ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties)}
    />
  ) : _getRequester(item, thisContext),
  Lifecycle: (
    <div className={`w-full truncate mr-4`} {...hoverTitle(item.TicketLifecycleName)}>
      <bdi>{item.TicketLifecycleName}</bdi>
    </div>
  ),
  Template: (
    <div className={`w-full truncate mr-4`} {...hoverTitle(item.TemplateName)}>
      <bdi>{item.TemplateName}</bdi>
    </div>
  ),
  Source: (
    <div className={`w-full truncate mr-4`} {...hoverTitle(item.SourceName)}>
      <bdi>{item.SourceName}</bdi>
    </div>
  ),
  Phase: (
    <>
      {item.TicketLifecyclePhaseName.length > 0 ? (
        <TicketLifecyclePhaseElement
          status={item["TicketLifecycle"]["Status"]}
          phaseName={item.TicketLifecyclePhaseName}
          titleWhenCut
        />
      ) : EMPTY_CELL}
    </>
  ),
  Tags: isInlineEditable(item, thisContext) ? (
    <InlineTagsCell
      ticketId={item.Id}
      tags={item.Tags ?? []}
      resting={tagsResting(item)}
      onSaved={thisContext._onTicketEdited}
      onPatch={(tags: TagRecord[]) => thisContext._patchRow(item.Id, { Tags: tags })}
      knownTags={thisContext.props.currentState.tags}
      guard={ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties)}
    />
  ) : tagsResting(item),
  Team: assignmentCell(item, thisContext, <span>{item.TeamName}</span>, 'team'),
  Group: assignmentCell(item, thisContext, <span>{item.GroupName}</span>, 'group'),
  Type: pickerCell(item, thisContext, 'TicketTypeId', t('ticket.ticket-list.type'), item.TicketTypeId, item.TicketType, thisContext.props.currentState.ticketTypes),
  Category: pickerCell(item, thisContext, 'CategoryId', t('ticket.ticket-list.category'), item.CategoryId, item.Category, thisContext.props.currentState.categories),
  Priority: pickerCell(item, thisContext, 'PriorityId', t('ticket.ticket-list.priority'), item.PriorityId, item.Priority, thisContext.props.currentState.priority),
  SLAs:
    item.TicketSlas?.length > 0 ? (
      <SLAPills ticketSlas={item.TicketSlas} />
    ) : (
      <>-</>
    ),
  "Last Modified Date": (
    <span>{ConvertDateToLocale(item.ModifiedDate, DateTime.DATE_SHORT)}</span>
  ),
  "Created Date": (
    <span>{ConvertDateToLocale(item.CreatedDate, DateTime.DATE_SHORT)}</span>
  ),
  'Due Date': dateCell(item, thisContext, 'DueDate', t('ticket.ticket-list.due-date'), item.DueDate),
  'Resolution Date': dateCell(item, thisContext, 'ResolutionDate', t('ticket.ticket-list.resolution-date'), item.ResolutionDate),
  Note: <MarkdownTooltip context={thisContext} details={item.Resolution?.Note ? item.Resolution?.Note : '\u00A0-'} />,
  InternalNote: <MarkdownTooltip context={thisContext} details={item.Resolution?.InternalNote?.length > 0 ? item.Resolution?.InternalNote : '\u00A0-'} /> ,
  DraftKB: <span>{item.Resolution?.DraftKB ? <AcceptIcon style={{paddingLeft: '25px'}}/> : EMPTY_CELL}</span>,
  ResolutionCategory: (
    <div className={`w-full truncate mr-4`} dir='rtl'>
      <bdi>{item.ResolutionCategory ? item.ResolutionCategory : '\u00A0-'}</bdi>
    </div>
  ),
  // Formatted through the shared duration renderer so the list agrees with the Time
  // Tracking tab and the Teams card. Zero renders as "0m" rather than a dash: adding this
  // column is a deliberate act, and a real zero is an answer where a dash reads as
  // "unknown". The column is only offered when time tracking is on.
  TotalAgentTime: (
    <span><DurationText minutes={item.TotalTimeSpentMinutes ?? 0} plain /></span>
  ),
});

const renderCPRecordValue =
  (customProperty: ICustomProperty, thisContext?: CellContext) => (item: TicketProp) =>
    thisContext ? cachedCell(item, thisContext, `cp:${customProperty.Id}`, () => drawCPRecordValue(customProperty, thisContext, item)) : drawCPRecordValue(customProperty, thisContext, item);

const drawCPRecordValue = (customProperty: ICustomProperty, thisContext: CellContext, item: TicketProp) => {
    const answerValues = JSON.parse((item.CustomPropertiesAnswerJson?.length ?? 0) > 0 ? item.CustomPropertiesAnswerJson :  "{}");
    const resting = <CustomPropertyResting answers={answerValues} property={customProperty} />;
    return thisContext && isInlineEditable(item, thisContext) ? (
      <InlineCustomPropertyCell
        ticketId={item.Id}
        property={customProperty}
        answers={answerValues}
        resting={resting}
        onSaved={thisContext._onTicketEdited}
        onPatch={(patch: Partial<TicketProp>) => thisContext._patchRow(item.Id, patch)}
        guard={ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties, customProperty.PropertyId)}
      />
    ) : resting;
  };

/**
 * A cell edits in place where the list asks for it (onTicketEdited), for an analyst, on a ticket that is not closed -
 * nor merged or deflected, which the ticket page cannot save at all.
 */
const isInlineEditable = (item: TicketProp, thisContext: CellContext) =>
  isStatusEditable(item, thisContext) && !item.Closed;

/**
 * Status follows the ticket page instead: still editable on a closed ticket, where changing it reopens the
 * ticket, but not on a merged or deflected one.
 */
const isStatusEditable = (item: TicketProp, thisContext: CellContext) =>
  !!thisContext.props.onTicketEdited && thisContext.state.analystAccess && cellEditingOn() && !item.IsMerged &&
  getStatus(item.StatusId, thisContext.props.currentState.ticketStatus)?.Guid !== STATUS_GUID.DEFLECTED;

/** Type, Category or Priority: typable in place where the row is editable, else the name as before. */
const pickerCell = (
  item: TicketProp, thisContext: CellContext, field: PickerField, label: string,
  valueId: number | null | undefined, displayName: string, records: TicketProps[]
) => isInlineEditable(item, thisContext) ? (
  <InlinePickerCell
    ticketId={item.Id}
    field={field}
    label={label}
    valueId={valueId ?? null}
    displayName={displayName}
    records={records}
    onSaved={thisContext._onTicketEdited}
    guard={ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties)}
  />
) : (
  <div className={`w-full truncate mr-4`} dir="rtl" {...hoverTitle(displayName)}>
    <bdi>{displayName}</bdi>
  </div>
);

/** Team, Group or Assignee: one panel for all four routing fields where the row is editable. */
const assignmentCell = (item: TicketProp, thisContext: CellContext, resting: React.ReactElement, column: AssignmentColumn) => isInlineEditable(item, thisContext) ? (
  <InlineAssignmentCell
    ticket={item}
    column={column}
    resting={resting}
    teams={thisContext.props.currentState.teams}
    groups={thisContext.props.currentState.supportgroups}
    usersInRole={thisContext.props.currentState.usersInRole}
    useTeam={thisContext.props.useTeam}
    onSaved={thisContext._onTicketEdited}
    onPatch={(patch: Partial<TicketProp>) => thisContext._patchRow(item.Id, patch)}
    guard={ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties)}
  />
) : resting;

/** Due or resolution date: a calendar under the cell where the row is editable; both can be emptied. */
const dateResting = (iso: string | null | undefined) => <span>{iso ? ConvertDateToLocale(iso, DateTime.DATE_SHORT) : EMPTY_CELL}</span>;

const dateCell = (
  item: TicketProp, thisContext: CellContext, field: 'DueDate' | 'ResolutionDate', label: string, value: string | null | undefined
) => isInlineEditable(item, thisContext) ? (
  <InlineDateCell
    rowKey={item.Id}
    label={label}
    value={value ? new Date(value) : null}
    clearable
    resting={dateResting(value)}
    restingFor={date => dateResting(date?.toISOString())}
    commit={committing<Date | null>(
      date => new platformService().updateTicket(item.Id, { [field]: date ? date.toISOString() : null })
        .then(() => thisContext._patchRow(item.Id, { [field]: date ? date.toISOString() : null })),
      thisContext._onTicketEdited,
      ticketSaveGuard(item.Id, false, thisContext.props.currentState.customProperties)
    )}
  />
) : dateResting(value);

const statusesWithArchived = (thisContext: CellContext) =>
  withArchived(thisContext.props.currentState.ticketStatus, thisContext.props.currentState.archivedTicketStatus);

const statusPill = (statusId: number | null, item: TicketProp, thisContext: CellContext) => statusId == null ? (
  <div className={`w-full truncate mr-4`} dir="rtl" {...hoverTitle(item.Status)}>
    <bdi>{item.Status}</bdi>
  </div>
) : (
  <StatusPillCell
    status={getStatus(statusId, statusesWithArchived(thisContext))}
    path={hierarchialStatusName({ statusId, ticketStatuses: statusesWithArchived(thisContext) })}
  />
);

type RowFields = {
  Id?: number;
  Tags?: { Id?: number }[];
  Title?: string;
  ModifiedDate?: unknown;
  StatusId?: number | null;
  CategoryId?: number | null;
  Category?: unknown;
  RequesterId?: number | null;
  AffectedUsers?: { PlatformUserId?: number }[];
  TicketCollaborators?: { PlatformUserId?: number }[];
  ResolutionId?: number | null;
  Resolution?: { ModifiedDate?: unknown } | null;
  TicketSlas?: unknown;
};

const affectedIds = (row: RowFields) => (row.AffectedUsers ?? []).map(a => a.PlatformUserId).join(',');
const collaboratorIds = (row: RowFields) => (row.TicketCollaborators ?? []).map(c => c.PlatformUserId).join(',');

// ModifiedDate moves on every save of the ticket, so it catches any field; the rest cover what a save can
// change without it - names resolved on the client, the affected users, and the resolution (Draft KB writes
// only the resolution row).
const tagsResting = (item: TicketProp) => <TagPills tags={item.Tags} />;

// Tags too: RemoveTags does not move the ticket's ModifiedDate, so a removal would otherwise never reach the screen.
const tagIds = (row: RowFields) => (row.Tags ?? []).map(tag => tag.Id).join(',');

const rowChanged = (prev: RowFields, next: RowFields) =>
  prev.Id !== next.Id || prev.Title !== next.Title || prev.ModifiedDate !== next.ModifiedDate || prev.StatusId !== next.StatusId ||
  prev.CategoryId !== next.CategoryId || prev.Category !== next.Category ||
  prev.RequesterId !== next.RequesterId || affectedIds(prev) !== affectedIds(next) || collaboratorIds(prev) !== collaboratorIds(next) ||
  prev.ResolutionId !== next.ResolutionId || prev.Resolution?.ModifiedDate !== next.Resolution?.ModifiedDate || tagIds(prev) !== tagIds(next) ||
  // After a cell save only the changed ticket's SLAs are fetched again (keepUnchangedSlas): they arrive on their own.
  prev.TicketSlas !== next.TicketSlas;

const renderRecordValue =
  (colName: TicketColumnNameType, thisContext: any) => (item: TicketProp) => {
    if (item.Title === "temp_ticket") return <SkeletonCell />;
    const entry = cellCacheOf(item, thisContext);
    entry.record ??= renderRecord(item, thisContext);
    return entry.record[colName];
  };

/**
 * A row's cells, drawn once and handed back unchanged while the ticket, the list's props and the agent's licence stay
 * the same. Dragging a column wider redraws every row, and React skips a cell whose element is the one it drew last
 * time - so only the widths move. Without it every drag step rebuilt every cell, and each cell rebuilt its whole row
 * (renderRecord) to use one column: ~170 ms a step with cells read-only, ~300 ms editable (90 rows, 32 columns).
 * A save replaces the row's object (_patchRow, a reload), and anything else a cell reads arrives in the list's props.
 */
interface CellCacheEntry {
  props: unknown;
  analystAccess: unknown;
  record?: { [x in TicketColumnNameType]: React.ReactElement };
  cells: Map<string, React.ReactElement>;
}
const cellCache = new WeakMap<TicketProp, CellCacheEntry>();

const cellCacheOf = (item: TicketProp, thisContext: CellContext): CellCacheEntry => {
  const cached = cellCache.get(item);
  if (cached?.props === thisContext.props && cached.analystAccess === thisContext.state.analystAccess) return cached;
  const entry: CellCacheEntry = { props: thisContext.props, analystAccess: thisContext.state.analystAccess, cells: new Map() };
  cellCache.set(item, entry);
  return entry;
};

const cachedCell = (item: TicketProp, thisContext: CellContext, key: string, draw: () => React.ReactElement): React.ReactElement => {
  const { cells } = cellCacheOf(item, thisContext);
  if (!cells.has(key)) cells.set(key, draw());
  return cells.get(key);
};

export const _getUser = (email: string, name: string) => (
  <div>
    {_getUserAvatar(email, name)}
    &nbsp;
    {!email ? t("ticket.ticket-board.ticket-card.unassigned") : name}
  </div>
);

export const _getUsers = (users: any[], title?: string) =>
  users.length > 0 ? (
    <span className="list-users-cell">
      <GroupUsersIcon maxLength={3} usersList={users} isListing={true} title={title} />
    </span>
  ) : (
    <span>-</span>
  );

export const _getUserAvatar = (email: string, name: string) => {
  return email ? (
    <UserAvatar
      {...{
        idOrUpn: email,
        avatarProps: { name: name, size: "smaller", className: "cui-ml-2" },
      }}
    />
  ) : (
    <SpeakerPersonIcon
      size="large"
      style={{ color: "#8f90a6" }}
      variables={{ largeSize: "24px" }}
    />
  );
};
